import { Box, Flex } from "@chakra-ui/react";
import { EthTokenIcon, CloudinaryImage, LooksTokenIcon } from "@looksrare/uikit";
import { formatToSignificant, formatUsd, fromDecimals, useCoinPrices } from "@looksrare/utils";
import { Text } from "uikit";
import { ImageData } from "types/graphql";
import { RafflePrize } from "views/win/userGeneratedRaffles/types";

interface NFTPrizeRowProps {
  image: ImageData;
  name: string;
  collectionName: string;
}

export const NFTPrizeRow = ({ image, name, collectionName }: NFTPrizeRowProps) => {
  return (
    <Flex gap={3} alignItems="center">
      <Box borderRadius="mini" overflow="hidden" width={12} height={12}>
        <CloudinaryImage
          src={image.src}
          contentType={image.contentType}
          objectFit="cover"
          alt={name}
          width={48}
          height={48}
        />
      </Box>
      <Box>
        <Text textStyle="display-body" bold>
          {name}
        </Text>
        <Text textStyle="detail" bold color="text-03">
          {collectionName}
        </Text>
      </Box>
    </Flex>
  );
};

interface CurrencyPrizeRowProps {
  tokenType: RafflePrize["tokenType"];
  amount: RafflePrize["amount"];
}

// decided to co-locate these components since they are so similar
export const CurrencyPrizeRow = ({ tokenType, amount }: CurrencyPrizeRowProps) => {
  const coinPriceQuery = useCoinPrices();
  const ethPriceUsd = coinPriceQuery.data ? coinPriceQuery.data.eth.price : 0;
  const looksPriceUsd = coinPriceQuery.data ? coinPriceQuery.data.looks.price : 0;

  const iconElement = (() => {
    if (tokenType === "ETH") {
      return <EthTokenIcon boxSize={12} />;
    }
    if (tokenType === "ERC20") {
      return <LooksTokenIcon boxSize={12} />;
    }
  })();

  const { tokenAmount, usdAmount } = (() => {
    if (tokenType === "ETH") {
      const ethAmountUsd = parseFloat(fromDecimals(amount)) * ethPriceUsd;
      return {
        tokenAmount: `${formatToSignificant(amount)} ETH`,
        usdAmount: `(${formatUsd(ethAmountUsd)})`,
      };
    }

    // tokenType === "ERC20" is the only other option for now
    const looksAmountUsd = parseFloat(fromDecimals(amount)) * looksPriceUsd;
    return {
      tokenAmount: `${formatToSignificant(amount)} LOOKS`,
      usdAmount: `(${formatUsd(looksAmountUsd)})`,
    };
  })();

  return (
    <Flex gap={3} alignItems="center">
      <Box borderRadius="mini" overflow="hidden" width={12} height={12}>
        {iconElement}
      </Box>
      <Box>
        <Text textStyle="display-body" bold>
          {tokenAmount}
        </Text>
        <Text textStyle="detail" bold color="text-03">
          {usdAmount}
        </Text>
      </Box>
    </Flex>
  );
};
